<template>
  <b-card>
    <div slot="header">
      <div class="row align-items-center">
        <div class="col-8">
          <h3 class="mb-0">{{ $t('event-registration-form.title') }}</h3>
        </div>
      </div>
    </div>
    <!-- 步驟進度條 -->
    <van-steps :active="current_step - 1" active-color="#5e72e4" class="mb-4" v-if="!submitted">
      <van-step>{{ $t('event-registration-form.basic.title') }}</van-step>
      <van-step>{{ $t('event-registration-form.detail.title') }}</van-step>
      <van-step>{{ $t('event-registration-form.confirm.title') }}</van-step>
    </van-steps>

    <ValidationObserver ref="observer" v-slot="{ handleSubmit }" v-if="!submitted">
      <form @submit.prevent="handleSubmit(onActualSubmit)">
        <!-- 第一步：基本資料 -->
        <div v-if="current_step === 1">
          <van-divider
            :style="{ color: 'blue', borderColor: 'blue', 'font-size': '1.5em' }"
          >{{ $t('event-registration-form.basic.title') }}</van-divider>
          <div class="pl-lg-4">
            <!-- 姓名 -->
            <div class="row">
              <div class="col-lg-6">
                <ValidationProvider :name="$t('event-registration-form.basic.label.name')" rules="required" v-slot="{ errors, valid }">
                  <b-form-group :label="$t('event-registration-form.basic.label.name')" label-for="input-name" :state="errors[0] ? false : (valid ? true : null)" :invalid-feedback="errors[0]">
                    <b-form-input id="input-name" autocomplete="name" v-model="form.name" :state="errors[0] ? false : (valid ? true : null)"></b-form-input>
                  </b-form-group>
                </ValidationProvider>
              </div>
            </div>

            <!-- 性別 / 生日 -->
            <div class="row">
              <div class="col-lg-6">
                <ValidationProvider :name="$t('event-registration-form.basic.label.gender')" rules="required" v-slot="{ errors, valid }">
                  <b-form-group :label="$t('event-registration-form.basic.label.gender')" label-for="input-gender" :state="errors[0] ? false : (valid ? true : null)" :invalid-feedback="errors[0]">
                    <b-form-select id="input-gender" v-model="form.gender" :state="errors[0] ? false : (valid ? true : null)">
                      <template v-if="constant">
                        <b-form-select-option :value="null" disabled>{{ $i18n.locale === 'tw' ? '請選擇性別' : 'Please select gender' }}</b-form-select-option>
                        <b-form-select-option v-for="(value, key) in constant.genders" :key="key" :value="key">
                          {{ $i18n.locale === 'tw' ? value.tw : value.en }}
                        </b-form-select-option>
                      </template>
                    </b-form-select>
                  </b-form-group>
                </ValidationProvider>
              </div>
              <div class="col-lg-6">
                <ValidationProvider :name="$t('event-registration-form.basic.label.birth-date')" rules="required" v-slot="{ errors, valid }">
                  <b-form-group :label="$t('event-registration-form.basic.label.birth-date')" label-for="input-birth-date" :state="errors[0] ? false : (valid ? true : null)" :invalid-feedback="errors[0]">
                    <b-form-input id="input-birth-date" type="date" autocomplete="bday" v-model="form.birth_date" :state="errors[0] ? false : (valid ? true : null)"></b-form-input>
                  </b-form-group>
                </ValidationProvider>
              </div>
            </div>

            <!-- 學歷 / 職業 -->
            <div class="row">
              <div class="col-lg-6">
                <b-form-group :label="$t('event-registration-form.basic.label.education')" label-for="input-education" >
                  <b-form-select id="input-education" v-model="form.education">
                    <template v-if="constant">
                      <b-form-select-option :value="null">{{ $i18n.locale === 'tw' ? '請選擇教育程度' : 'Please select education level' }}</b-form-select-option>
                      <b-form-select-option v-for="(value, key) in constant.educations" :key="key" :value="key">
                        {{ $i18n.locale === 'tw' ? value.tw : value.en }}
                      </b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>
              </div>
              <div class="col-lg-6">
                <b-form-group :label="$t('event-registration-form.basic.label.occupation')" label-for="input-occupation">
                  <b-form-input id="input-occupation" v-model="form.occupation"></b-form-input>
                </b-form-group>
              </div>
            </div>

            <!-- 健康狀況 -->
            <div class="row">
              <div class="col-md-12">
                <ValidationProvider :name="$t('event-registration-form.basic.label.health-condition')" rules="required" v-slot="{ errors }">
                  <b-form-group :label="$t('event-registration-form.basic.label.health-condition')" :state="errors[0] ? false : null" :invalid-feedback="errors[0]">
                    <b-form-checkbox-group v-model="ui.health_options" :state="errors[0] ? false : null">
                      <template v-if="constant">
                        <b-form-checkbox 
                          v-for="(value, key) in constant.health_conditions" 
                          :key="key" 
                          :value="key"
                          v-if="!['chronic_disease', 'special_disease'].includes(key)"
                        >
                          {{ $i18n.locale === 'tw' ? value.tw : value.en }}
                        </b-form-checkbox>
                      </template>
                    </b-form-checkbox-group>
                    <template v-if="constant && constant.health_conditions.chronic_disease">
                      <div class="mt-2">
                        <div class="d-flex align-items-center">
                          <b-form-checkbox value="chronic_disease" v-model="ui.health_options" :state="errors[0] ? false : null" class="mr-2">
                            {{ $i18n.locale === 'tw' ? constant.health_conditions.chronic_disease.tw : constant.health_conditions.chronic_disease.en }}
                          </b-form-checkbox>
                          <ValidationProvider :name="$i18n.locale === 'tw' ? constant.health_conditions.chronic_disease.tw : constant.health_conditions.chronic_disease.en" :rules="{ required_if_checked: { target: ui.health_options.includes('chronic_disease') } }" v-slot="{ errors: chronic_disease_errors }">
                            <b-form-input v-model="ui.health_chronic.value" :placeholder="$t('event-registration-form.basic.placeholder.please-specify')" :disabled="!ui.health_options.includes('chronic_disease')" :state="chronic_disease_errors[0] ? false : null" size="sm" style="width: auto; flex: 1;"></b-form-input>
                            <b-form-invalid-feedback :state="chronic_disease_errors[0] ? false : null">{{ chronic_disease_errors[0] }}</b-form-invalid-feedback>
                          </ValidationProvider>
                        </div>
                      </div>
                    </template>
                    <template v-if="constant && constant.health_conditions.special_disease">
                      <div class="mt-2">
                        <div class="d-flex align-items-center">
                          <b-form-checkbox value="special_disease" v-model="ui.health_options" :state="errors[0] ? false : null" class="mr-2">
                            {{ $i18n.locale === 'tw' ? constant.health_conditions.special_disease.tw : constant.health_conditions.special_disease.en }}
                          </b-form-checkbox>
                          <ValidationProvider :name="$i18n.locale === 'tw' ? constant.health_conditions.special_disease.tw : constant.health_conditions.special_disease.en" :rules="{ required_if_checked: { target: ui.health_options.includes('special_disease') } }" v-slot="{ errors: special_disease_errors }">
                            <b-form-input v-model="ui.health_special.value" :placeholder="$t('event-registration-form.basic.placeholder.please-specify')" :disabled="!ui.health_options.includes('special_disease')" :state="special_disease_errors[0] ? false : null" size="sm" style="width: auto; flex: 1;"></b-form-input>
                            <b-form-invalid-feedback :state="special_disease_errors[0] ? false : null">{{ special_disease_errors[0] }}</b-form-invalid-feedback>
                          </ValidationProvider>
                        </div>
                      </div>
                    </template>
                  </b-form-group>
                </ValidationProvider>
              </div>
            </div>
          </div>
          <div class="pl-lg-4">
            <!-- 聯絡資訊 -->
            <h5 style="color:maroon">{{ $t('event-registration-form.basic.heading.contact') }}</h5>
            <div class="row">
              <div class="col-lg-6">
                <ValidationProvider :name="$t('event-registration-form.basic.label.email')" rules="required|email" v-slot="{ errors, valid }">
                  <b-form-group :label="$t('event-registration-form.basic.label.email')" label-for="input-email" :state="errors[0] ? false : (form.email ? true : null)" :invalid-feedback="errors[0]">
                    <b-form-input id="input-email" type="email" autocomplete="email" v-model="form.email" :state="errors[0] ? false : (form.email ? true : null)"></b-form-input>
                  </b-form-group>
                </ValidationProvider>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-12">
                <p class="text-muted small mb-2">
                  <i class="fa fa-info-circle mr-1"></i>
                  {{ $t('event-registration-form.basic.instruction-text.phone-required') }}
                </p>
              </div>
              <div class="col-lg-6">
                <ValidationProvider :name="$t('event-registration-form.basic.label.phone')" :rules="{ regex: /^[\d\s\-\+\(\)]+$/, required: !form.mobile_phone }" v-slot="{ errors, valid }">
                  <b-form-group :label="$t('event-registration-form.basic.label.phone')" label-for="input-phone" :state="errors[0] ? false : (valid ? true : null)" :invalid-feedback="errors[0]">
                    <b-form-input id="input-phone" autocomplete="tel" v-model="form.phone" :state="errors[0] ? false : (valid ? true : null)"></b-form-input>
                  </b-form-group>
                </ValidationProvider>
              </div>
              <div class="col-lg-6">
                <ValidationProvider :name="$t('event-registration-form.basic.label.mobile-phone')" :rules="{ regex: /^[\d\s\-\+\(\)]+$/, required: !form.phone }" v-slot="{ errors, valid }">
                  <b-form-group :label="$t('event-registration-form.basic.label.mobile-phone')" label-for="input-mobile-phone" :state="errors[0] ? false : (valid ? true : null)" :invalid-feedback="errors[0]">
                    <b-form-input id="input-mobile-phone" autocomplete="tel" v-model="form.mobile_phone" :state="errors[0] ? false : (valid ? true : null)"></b-form-input>
                  </b-form-group>
                </ValidationProvider>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <ValidationProvider :name="$t('event-registration-form.basic.label.address')" rules="required" v-slot="{ errors, valid }">
                  <b-form-group :label="$t('event-registration-form.basic.label.address')" label-for="input-address" :state="errors[0] ? false : (valid ? true : null)" :invalid-feedback="errors[0]">
                    <b-form-input id="input-address" v-model="form.address" :state="errors[0] ? false : (valid ? true : null)"></b-form-input>
                  </b-form-group>
                </ValidationProvider>
              </div>
            </div>
            <!-- 照片上傳暫時隱藏，相關邏輯先註解，待確認後再啟用
            <div class="row">
              <div class="col-md-12">
                <b-form-group :label="$t('event-registration-form.photo')" label-for="input-photo">
                  <b-form-file
                    id="input-photo"
                    v-model="ui.photo"
                    :placeholder="$t('event-registration-form.photo_hint')"
                    drop-placeholder="Drop file here..."
                    accept="image/*"
                    @change="handlePhotoChange"
                  ></b-form-file>
                </b-form-group>
              </div>
            </div>
            -->
            <!-- 緊急聯絡人 -->
            <h5 style="color:purple">{{ $t('event-registration-form.basic.heading.emergency-contact') }}</h5>
            <div class="row">
              <div class="col-lg-6">
                 <ValidationProvider :name="$t('event-registration-form.basic.label.emergency-contact-name')" rules="required" v-slot="{ errors, valid }">
                   <b-form-group :label="$t('event-registration-form.basic.label.emergency-contact-name')" label-for="input-emergency-name" :state="errors[0] ? false : (valid ? true : null)" :invalid-feedback="errors[0]">
                     <b-form-input id="input-emergency-name" v-model="form.emergency_contact_name" :state="errors[0] ? false : (valid ? true : null)"></b-form-input>
                   </b-form-group>
                 </ValidationProvider>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-12">
                <p class="text-muted small mb-2">
                  <i class="fa fa-info-circle mr-1"></i>
                  {{ $t('event-registration-form.basic.instruction-text.phone-required') }}
                </p>
              </div>
              <div class="col-lg-6">
                <ValidationProvider :name="$t('event-registration-form.basic.label.emergency-contact-phone')" :rules="{ regex: /^[\d\s\-\+\(\)]+$/, required: !form.emergency_contact_mobile_phone }" v-slot="{ errors, valid }">
                  <b-form-group :label="$t('event-registration-form.basic.label.emergency-contact-phone')" label-for="input-emergency-phone" :state="errors[0] ? false : (valid ? true : null)" :invalid-feedback="errors[0]">
                    <b-form-input id="input-emergency-phone" v-model="form.emergency_contact_phone" :state="errors[0] ? false : (valid ? true : null)"></b-form-input>
                  </b-form-group>
                </ValidationProvider>
              </div>
              <div class="col-lg-6">
                <ValidationProvider :name="$t('event-registration-form.basic.label.emergency-contact-mobile-phone')" :rules="{ regex: /^[\d\s\-\+\(\)]+$/, required: !form.emergency_contact_phone }" v-slot="{ errors, valid }">
                  <b-form-group :label="$t('event-registration-form.basic.label.emergency-contact-mobile-phone')" label-for="input-emergency-mobile" :state="errors[0] ? false : (valid ? true : null)" :invalid-feedback="errors[0]">
                    <b-form-input id="input-emergency-mobile" v-model="form.emergency_contact_mobile_phone" :state="errors[0] ? false : (valid ? true : null)"></b-form-input>
                  </b-form-group>
                </ValidationProvider>
              </div>
              <!-- 暫時隱藏，相關邏輯先註解，待確認後再啟用
              <div class="col-lg-6">
                <b-form-group :label="$t('event-registration-form.basic.label.emergency-contact-address')" label-for="input-emergency-address">
                  <b-form-input id="input-emergency-address" v-model="form.emergency_contact_address"></b-form-input>
                </b-form-group>
              </div> -->
            </div>
          </div>
        </div>

        <!-- 第二步：詳細資料 -->
        <div v-if="current_step === 2">
          <van-divider
            :style="{ color: 'teal', borderColor: 'teal', 'font-size': '1.5em' }"
          >{{ $t('event-registration-form.detail.title') }}</van-divider>
          <div class="pl-lg-4">
            <!-- 受戒狀況 -->
            <div class="row">
              <div class="col-lg-12">
                <b-form-group :label="$t('event-registration-form.detail.label.precepts')">
                  <!-- <b-form-checkbox v-model="ui.precepts_none" class="mb-2">{{ $t('event-registration-form.detail.option.precepts.bodhisattva_none') }}</b-form-checkbox> --><!-- 需求待確認，先保留 -->
                  <b-form-checkbox-group v-model="ui.precepts_options">
                    <template v-if="constant">
                      <b-form-checkbox :key="key" :value="key" v-for="(value, key) in constant.precepts">
                        {{ $i18n.locale === 'tw' ? value.tw : value.en }}
                      </b-form-checkbox>
                    </template>
                  </b-form-checkbox-group>
                  <ValidationProvider
                    v-if="ui.precepts_options.includes('full_ordination_precepts')"
                    :name="$t('event-registration-form.detail.label.full-ordination-date')"
                    :rules="{ required_if_checked: { target: ui.precepts_options.includes('full_ordination_precepts') } }"
                    v-slot="{ errors: full_ordination_precepts_errors }"
                  >
                    <div class="mt-3">
                      <label class="d-block mb-2">{{ $t('event-registration-form.detail.label.full-ordination-date') }}</label>
                      <flat-picker
                        v-model="ui.precepts_ordination_date"
                        :config="{ allowInput: true, dateFormat: 'Y-m-d' }"
                        class="form-control"
                        :placeholder="$t('event-registration-form.detail.placeholder.full-ordination-date')"
                      />
                      <b-form-invalid-feedback :state="full_ordination_precepts_errors[0] ? false : null">{{ full_ordination_precepts_errors[0] }}</b-form-invalid-feedback>
                    </div>
                  </ValidationProvider>
                  <!-- <div class="mt-2">
                    <div class="d-flex align-items-center">
                      <b-form-checkbox v-model="ui.precepts_history.checked" class="mr-2">{{ $t('event-registration-form.detail.option.precepts.bodhisattva_history') }}</b-form-checkbox>
                      <b-form-input type="number" v-model="ui.precepts_history.value" :disabled="!ui.precepts_history.checked" :state="errors.precepts_history ? false : null" size="sm" style="width: 80px;" class="mr-2"></b-form-input>
                      <span>{{ $t('event-registration-form.detail.option.precepts.bodhisattva_times') }}</span>
                    </div>
                    <b-form-invalid-feedback :state="errors.precepts_history ? false : null">{{ errors.precepts_history }}</b-form-invalid-feedback>
                  </div> --><!-- 需求待確認，先保留 -->
                </b-form-group>
              </div>
            </div>

            <!-- 受持八關齋戒狀況 -->
            <div class="row">
              <div class="col-lg-12">
                <b-form-group :label="$t('event-registration-form.detail.label.observing-eight-precepts')">
                  <b-form-radio-group id="observing-eight-precepts" v-model="form.observing_eight_precepts">
                    <b-form-radio :key="key" :value="key" v-for="(value, key) in constant.observing_eight_precepts_options">{{ $i18n.locale === 'tw' ? value.tw : value.en }}</b-form-radio>
                  </b-form-radio-group>
                </b-form-group>
              </div>
            </div>

            <!-- 專長 -->
            <div class="row">
              <div class="col-lg-12">
                <b-form-group :label="$t('event-registration-form.detail.label.skills')">
                  <b-form-checkbox-group v-model="ui.skills_options">
                    <template v-if="constant">
                      <b-form-checkbox :key="key" :value="key" v-for="(value, key) in constant.skills" v-if="key !== 'other'">
                        {{ $i18n.locale === 'tw' ? value.tw : value.en }}
                      </b-form-checkbox>
                    </template>
                  </b-form-checkbox-group>
                  <template v-if="constant && constant.skills.other">
                    <div class="mt-2">
                      <div class="d-flex align-items-center">
                        <b-form-checkbox value="other" v-model="ui.skills_options" class="mr-2">
                          {{ $i18n.locale === 'tw' ? constant.skills.other.tw : constant.skills.other.en }}
                        </b-form-checkbox>
                        <ValidationProvider :name="$i18n.locale === 'tw' ? constant.skills.other.tw : constant.skills.other.en" :rules="{ required_if_checked: { target: ui.skills_options.includes('other') } }" v-slot="{ errors: skill_other_errors }">
                          <b-form-input v-model="ui.skills_other.value" :placeholder="$t('event-registration-form.detail.placeholder.please-specify')" :disabled="!ui.skills_options.includes('other')" :state="skill_other_errors[0] ? false : null" size="sm" style="width: auto; flex: 1;"></b-form-input>
                          <b-form-invalid-feedback :state="skill_other_errors[0] ? false : null">{{ skill_other_errors[0] }}</b-form-invalid-feedback>
                        </ValidationProvider>
                      </div>
                    </div>
                  </template>
                </b-form-group>
              </div>
            </div>
            
            <!-- 有意願擔任義工 -->
            <div class="row">
              <div class="col-md-12 mb-3">
                <p class="mb-2 small">{{ $t('event-registration-form.detail.label.volunteer-interest') }}</p>
                <b-form-radio-group id="volunteer-interest" v-model="form.volunteer_interest">
                  <b-form-radio value="Y">{{ $i18n.locale === 'tw' ? '有' : 'Yes' }}</b-form-radio>
                  <b-form-radio value="N">{{ $i18n.locale === 'tw' ? '無' : 'No' }}</b-form-radio>
                </b-form-radio-group>
              </div>
            </div>

            <!-- 曾經擔任義工或職員角色 -->
            <div class="row">
              <div class="col-md-12 mb-3">
                <b-form-group :label="$t('event-registration-form.detail.label.volunteer-or-staff-role')" label-for="input-volunteer-or-staff-role">
                  <b-form-input id="input-volunteer-or-staff-role" v-model="form.volunteer_or_staff_role"></b-form-input>
                </b-form-group>
              </div>
            </div>
            
            <!-- 消息來源 -->
            <div class="row">
              <div class="col-md-12">
                <b-form-group :label="$t('event-registration-form.detail.label.how-heard')">
                  <b-form-checkbox-group v-model="ui.how_heard_options">
                    <template v-if="constant">
                      <b-form-checkbox v-for="(value, key) in constant.referral_sources" :key="key" :value="key" v-if="key !== 'other'">
                        {{ $i18n.locale === 'tw' ? value.tw : value.en }}
                      </b-form-checkbox>
                    </template>
                  </b-form-checkbox-group>
                  <template v-if="constant && constant.referral_sources.other">
                    <div class="mt-2">
                      <div class="d-flex align-items-center">
                        <b-form-checkbox value="other" v-model="ui.how_heard_options" class="mr-2">
                          {{ $i18n.locale === 'tw' ? constant.referral_sources.other.tw : constant.referral_sources.other.en }}
                        </b-form-checkbox>
                        <ValidationProvider :name="$i18n.locale === 'tw' ? constant.referral_sources.other.tw : constant.referral_sources.other.en" :rules="{ required_if_checked: { target: ui.how_heard_options.includes('other') } }" v-slot="{ errors: how_heard_other_errors }">
                          <b-form-input v-model="ui.how_heard_other.value" :placeholder="$t('event-registration-form.detail.placeholder.please-specify')" :disabled="!ui.how_heard_options.includes('other')" :state="how_heard_other_errors[0] ? false : null" size="sm" style="width: auto; flex: 1;"></b-form-input>
                          <b-form-invalid-feedback :state="how_heard_other_errors[0] ? false : null">{{ how_heard_other_errors[0] }}</b-form-invalid-feedback>
                        </ValidationProvider>
                      </div>
                    </div>
                  </template>
                </b-form-group>
              </div>
            </div>

            <!-- 參加次數 -->
            <div class="row">
              <div class="col-lg-6">
                <ValidationProvider :name="$t('event-registration-form.detail.label.attended-times')" rules="required|numeric" v-slot="{ errors, valid }">
                  <b-form-group :label="$t('event-registration-form.detail.label.attended-times')" label-for="input-attended-times" :state="errors[0] ? false : (valid ? true : null)" :invalid-feedback="errors[0]">
                    <b-form-input id="input-attended-times" type="number" v-model.number="form.attended_times" :state="errors[0] ? false : (valid ? true : null)"></b-form-input>
                  </b-form-group>
                </ValidationProvider>
              </div>
            </div>
            
            <!-- 交通方式 -->
            <h5 style="color:maroon">{{ $t('event-registration-form.detail.heading.transportation') }}</h5>
            <div class="row">
              <div class="col-lg-12">
                <ValidationProvider :name="$t('event-registration-form.detail.label.transportation-go')" rules="required" v-slot="{ errors, valid }">
                  <b-form-group :label="$t('event-registration-form.detail.label.transportation-go')" :state="errors[0] ? false : (valid ? true : null)" :invalid-feedback="errors[0]">
                    <b-form-radio-group name="transportation_go" v-model="form.transportation_go" :state="errors[0] ? false : (valid ? true : null)">
                      <template v-if="constant">
                        <b-form-radio :value="key" v-for="(value, key) in constant.transportation_methods" :key="key">
                          {{ $i18n.locale === 'tw' ? value.tw : value.en }}
                        </b-form-radio>
                      </template>
                    </b-form-radio-group>
                  </b-form-group>
                </ValidationProvider>
              </div>
              <div class="col-lg-12">
                <ValidationProvider :name="$t('event-registration-form.detail.label.transportation-return')" rules="required" v-slot="{ errors, valid }">
                  <b-form-group :label="$t('event-registration-form.detail.label.transportation-return')" :state="errors[0] ? false : (valid ? true : null)" :invalid-feedback="errors[0]">
                    <b-form-radio-group v-model="form.transportation_return" name="transportation_return" :state="errors[0] ? false : (valid ? true : null)">
                      <template v-if="constant">
                        <b-form-radio :value="key" v-for="(value, key) in constant.transportation_methods" :key="key">
                          {{ $i18n.locale === 'tw' ? value.tw : value.en }}
                        </b-form-radio>
                      </template>
                    </b-form-radio-group>
                  </b-form-group>
                </ValidationProvider>
              </div>
            </div>

            <!-- 備註 -->
            <div class="row">
              <div class="col-md-12 mb-3">
                <b-form-group :label="$t('event-registration-form.detail.label.remarks')" label-for="input-remarks">
                  <b-form-textarea id="input-remarks" v-model="form.applicant_remarks" rows="3"></b-form-textarea>
                </b-form-group>
              </div>
            </div>

            <!-- 承諾 -->
            <div class="row">
              <div class="col-md-12 mb-3">
                <b-form-group :label="$t('event-registration-form.detail.label.agreement')" label-for="agreement-signed">
                  <div class="border rounded p-3 bg-light">
                    <p class="mb-2 small">{{ $t('event-registration-form.detail.declaration-text.agreement') }}</p>
                    <b-form-radio-group id="agreement-signed" v-model="form.agreement_signed" @change="handleAgreementChange">
                      <b-form-radio value="Y">{{ $t('event-registration-form.detail.option.agreement.agree') }}</b-form-radio>
                      <b-form-radio value="N">{{ $t('event-registration-form.detail.option.agreement.disagree') }}</b-form-radio>
                    </b-form-radio-group>
                  </div>
                </b-form-group>
              </div>
            </div>
          </div>
        </div>

        <!-- 第三步：確認預覽 -->
        <div v-if="current_step === 3">
          <van-divider
            :style="{ color: 'orange', borderColor: 'orange', 'font-size': '1.5em' }"
          >{{ $t('event-registration-form.confirm.title') }}</van-divider>
          <div class="pl-lg-4 pb-4">
            <!-- 基本資料 -->
            <h5 class="text-primary mb-3">
              <i class="fa fa-user-circle mr-2"></i>
              {{ $t('event-registration-form.basic.title') }}
            </h5>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.basic.label.name') }}:</b-col>
              <b-col sm="9" class="font-weight-bold">{{ form.name }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.basic.label.gender') }}:</b-col>
              <b-col sm="9">{{ constant && constant.genders[form.gender] ? ($i18n.locale === 'tw' ? constant.genders[form.gender].tw : constant.genders[form.gender].en) : form.gender }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.basic.label.birth-date') }}:</b-col>
              <b-col sm="9">{{ form.birth_date || '-' }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.basic.label.education') }}:</b-col>
              <b-col sm="9">{{ constant && form.education && constant.educations[form.education] ? ($i18n.locale === 'tw' ? constant.educations[form.education].tw : constant.educations[form.education].en) : (form.education || '-') }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.basic.label.occupation') }}:</b-col>
              <b-col sm="9">{{ form.occupation || '-' }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.basic.label.health-condition') }}:</b-col>
              <b-col sm="9">
                <div v-if="ui.health_options.length > 0">
                  <span v-for="(key, index) in ui.health_options" :key="key">
                    <template v-if="constant && constant.health_conditions[key]">
                      {{ $i18n.locale === 'tw' ? constant.health_conditions[key].tw : constant.health_conditions[key].en }}
                      <span v-if="key === 'chronic_disease' && ui.health_chronic.value" class="text-secondary small">({{ ui.health_chronic.value }})</span>
                      <span v-if="key === 'special_disease' && ui.health_special.value" class="text-secondary small">({{ ui.health_special.value }})</span>
                    </template>
                    <span v-if="index < ui.health_options.length - 1">、</span>
                  </span>
                </div>
                <div v-else>-</div>
              </b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.basic.label.email') }}:</b-col>
              <b-col sm="9">{{ form.email || '-' }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.basic.label.mobile-phone') }}:</b-col>
              <b-col sm="9">{{ form.mobile_phone }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.basic.label.phone') }}:</b-col>
              <b-col sm="9">{{ form.phone || '-' }}</b-col>
            </b-row>
            <b-row class="mb-3">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.basic.label.address') }}:</b-col>
              <b-col sm="9">{{ form.address }}</b-col>
            </b-row>

            <!-- 緊急聯絡人 -->
            <h5 class="text-primary mb-3 mt-4">
              <i class="fa fa-exclamation-triangle mr-2"></i>
              {{ $t('event-registration-form.basic.heading.emergency-contact') }}
            </h5>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.basic.label.emergency-contact-name') }}:</b-col>
              <b-col sm="9">{{ form.emergency_contact_name }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.basic.label.emergency-contact-mobile-phone') }}:</b-col>
              <b-col sm="9">{{ form.emergency_contact_mobile_phone }}</b-col>
            </b-row>
            <!-- 暫時隱藏，相關邏輯先註解，待確認後再啟用
            <b-row class="mb-3">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.basic.emergency_contact.label.address') }}:</b-col>
              <b-col sm="9">{{ form.emergency_contact_address || '-' }}</b-col>
            </b-row> -->

            <!-- 詳細資料 -->
            <h5 class="text-primary mb-3 mt-4">
              <i class="fa fa-info-circle mr-2"></i>
              {{ $t('event-registration-form.detail.title') }}
            </h5>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.detail.label.precepts') }}:</b-col>
              <b-col sm="9">
                <div>
                  <!-- <span v-if="ui.precepts_none">{{ $t('event-registration-form.precepts_none') }}</span> -->
                  <!-- <span v-if="ui.precepts_none && ui.precepts_options.length > 0">、</span> -->
                  <span v-for="(key, index) in ui.precepts_options" :key="key">
                    <template v-if="constant && constant.precepts[key]">
                      {{ $i18n.locale === 'tw' ? constant.precepts[key].tw : constant.precepts[key].en }}
                      <template v-if="key === 'full_ordination_precepts' && ui.precepts_ordination_date">
                        ({{ ui.precepts_ordination_date }})
                      </template>
                    </template>
                    <span v-if="index < ui.precepts_options.length - 1">、</span>
                  </span>
                  <!-- <div v-if="ui.precepts_history.checked" class="mt-1">
                    {{ $t('event-registration-form.precepts_history') }} {{ ui.precepts_history.value }} {{ $t('event-registration-form.precepts_times') }}
                  </div> -->
                </div>
              </b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.detail.label.observing-eight-precepts') }}:</b-col>
              <b-col sm="9">
                <template v-if="constant && constant.observing_eight_precepts_options[form.observing_eight_precepts]">
                  {{ $i18n.locale === 'tw' ? constant.observing_eight_precepts_options[form.observing_eight_precepts].tw : constant.observing_eight_precepts_options[form.observing_eight_precepts].en }}
                </template>
              </b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.detail.label.skills') }}:</b-col>
              <b-col sm="9">
                <div v-if="ui.skills_options.length > 0">
                  <span v-for="(key, index) in ui.skills_options" :key="key">
                    <template v-if="constant && constant.skills[key]">
                      {{ $i18n.locale === 'tw' ? constant.skills[key].tw : constant.skills[key].en }}
                      <span v-if="key === 'other' && ui.skills_other.value" class="text-secondary small">({{ ui.skills_other.value }})</span>
                    </template>
                    <span v-if="index < ui.skills_options.length - 1">、</span>
                  </span>
                </div>
                <div v-else>-</div>
              </b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.detail.label.volunteer-interest') }}:</b-col>
              <b-col sm="9">{{ form.volunteer_interest === 'Y' ? ($i18n.locale === 'tw' ? '有' : 'Yes') : ($i18n.locale === 'tw' ? '無' : 'No') }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.detail.label.volunteer-or-staff-role') }}:</b-col>
              <b-col sm="9">{{ form.volunteer_or_staff_role || '-' }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.detail.label.how-heard') }}:</b-col>
              <b-col sm="9">
                <div v-if="ui.how_heard_options.length > 0">
                  <span v-for="(key, index) in ui.how_heard_options" :key="key">
                    <template v-if="constant && constant.referral_sources[key]">
                      {{ $i18n.locale === 'tw' ? constant.referral_sources[key].tw : constant.referral_sources[key].en }}
                      <span v-if="key === 'other' && ui.how_heard_other.value" class="text-secondary small">({{ ui.how_heard_other.value }})</span>
                    </template>
                    <span v-if="index < ui.how_heard_options.length - 1">、</span>
                  </span>
                </div>
                <div v-else>-</div>
              </b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.detail.label.attended-times') }}:</b-col>
              <b-col sm="9">{{ form.attended_times }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.detail.heading.transportation') }}:</b-col>
              <b-col sm="9">
                <template v-if="constant">
                  {{ $t('event-registration-form.detail.label.transportation-go') }}: {{ constant.transportation_methods[form.transportation_go] ? ($i18n.locale === 'tw' ? constant.transportation_methods[form.transportation_go].tw : constant.transportation_methods[form.transportation_go].en) : form.transportation_go }} /
                  {{ $t('event-registration-form.detail.label.transportation-return') }}: {{ constant.transportation_methods[form.transportation_return] ? ($i18n.locale === 'tw' ? constant.transportation_methods[form.transportation_return].tw : constant.transportation_methods[form.transportation_return].en) : form.transportation_return }}
                </template>
                <template v-else>-</template>
              </b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.detail.label.remarks') }}:</b-col>
              <b-col sm="9" style="white-space: pre-wrap;">{{ form.applicant_remarks || '-' }}</b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.detail.label.agreement') }}:</b-col>
              <b-col sm="9">{{ form.agreement_signed === 'Y' ? $t('event-registration-form.detail.option.agreement.agree') : $t('event-registration-form.detail.option.agreement.disagree') }}</b-col>
            </b-row>
            <!-- 照片預覽暫時隱藏，相關邏輯先註解，待確認後再啟用
            <b-row class="mb-4" v-if="ui.photo_preview">
              <b-col sm="3" class="text-muted">{{ $t('event-registration-form.photo') }}:</b-col>
              <b-col sm="9">
                <img :src="ui.photo_preview" style="max-width: 120px; max-height: 120px; border-radius: 8px; border: 1px solid #ddd;" class="mt-2" />
              </b-col>
            </b-row>
            -->

            <van-divider></van-divider>
            <p class="text-info small text-center"><i class="fa fa-info-circle mr-1"></i>{{ $t('event-registration-form.confirm.content') }}</p>
          </div>
        </div>
      
        <div class="col-12 text-center mt-5">
          <b-button v-show="current_step > 1" type="button" variant="outline-primary" @click="prevStep" class="mr-2">{{ $t('event-registration-form.button.prev-step.label') }}</b-button>
          <b-button v-show="current_step < 3" type="button" variant="primary" @click="nextStep">{{ $t('event-registration-form.button.next-step.label') }}</b-button>
          <b-button v-show="current_step === 3" type="submit" variant="success" :disabled="loading">{{ $t('event-registration-form.button.submit.label') }}</b-button>
        </div>
      </form>
    </ValidationObserver>

    <!-- 報名成功訊息頁面 -->
    <div v-else class="text-center py-5">
      <div class="mb-4">
        <i class="fa fa-check-circle text-success" style="font-size: 5rem;"></i>
      </div>
      <h2 class="text-success mb-3">{{ $t('event-registration-form.success.title') }}</h2>
      <p class="lead px-lg-5">{{ $t('event-registration-form.success.message') }}</p>
      <div class="mt-5">
        <b-button variant="primary" :to="{ name: 'latest-events' }">{{ $t('event.detail.button.view-more.label') }}</b-button>
        <b-button variant="danger" :to="{ name: 'index' }">{{ $i18n.locale === 'tw' ? '回首頁' : 'Back to Home' }}</b-button>
      </div>
    </div>
  </b-card>
</template>

<script>
import Vue from 'vue'

import {
  CardPlugin,
  FormGroupPlugin,
  FormInputPlugin,
  FormSelectPlugin,
  FormTextareaPlugin,
  FormCheckboxPlugin,
  FormRadioPlugin,
  FormFilePlugin,
  ButtonPlugin,
  LayoutPlugin
} from 'bootstrap-vue'
import { Steps, Step, Divider } from 'vant'
import { ValidationObserver, ValidationProvider, extend, localize } from 'vee-validate';
import * as rules from 'vee-validate/dist/rules';
import zh_TW from 'vee-validate/dist/locale/zh_TW.json';
import en from 'vee-validate/dist/locale/en.json';
import dayjs from 'dayjs'
import flatPicker from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'

import ConstantApiService from '@/services/budaedu-api-services/constant-api-service.js'
import EventRegistrationApiService from '@/services/budaedu-api-services/event-registration-api-service.js'

// 註冊所有 Vee-Validate 內建規則
Object.keys(rules).forEach(rule => {
  extend(rule, rules[rule]);
});

// 定義語系包
const locales = {
  tw: {
    ...zh_TW,
    messages: {
      ...zh_TW.messages,
      required_if_checked: '此欄位為必填'
    }
  },
  en: {
    ...en,
    messages: {
      ...en.messages,
      required_if_checked: 'The field is required'
    }
  }
}; // console.log(locales);

// 初始語系配置
if (window.app && window.app.$i18n) {
  localize(window.app.$i18n.locale === 'tw' ? 'zh_TW' : 'en', locales[window.app.$i18n.locale] || zh_TW);
  console.log('init have window.app.$i18n');
} else {
  localize('zh_TW', zh_TW);
  console.log('init do not have window.app.$i18n');
}

// 自定義規則：如果是特定選項則必填
extend('required_if_checked', {
  computesRequired: true,
  params: ['target'],
  validate(value, { target }) {
    if (target) {
      // console.log('target', target);
      // console.log('value', value);
      return {
        valid: !!(value && String(value).trim()),
        required: true
      };
    }
    return {
      valid: true,
      required: false
    };
  }
});

Vue.use(CardPlugin)
Vue.use(Steps)
Vue.use(Step)
Vue.use(Divider)
Vue.use(LayoutPlugin)
Vue.use(FormGroupPlugin)
Vue.use(FormInputPlugin)
Vue.use(FormSelectPlugin)
Vue.use(FormTextareaPlugin)
Vue.use(FormCheckboxPlugin)
Vue.use(FormRadioPlugin)
Vue.use(FormFilePlugin)
Vue.use(ButtonPlugin)

Vue.use(ConstantApiService)
Vue.use(EventRegistrationApiService)

export default {
  name: 'event-registration-form',
  components: {
    ValidationObserver,
    ValidationProvider,
    flatPicker
  },
  props: {
    eventId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      current_step: 1,
      submitted: false,
      loading: false,
      form: {
        event_id: this.eventId,
        name: '',
        email: '',
        phone: '',
        mobile_phone: '',
        address: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        emergency_contact_mobile_phone: '',
        // emergency_contact_address: '', // 暫時隱藏，相關邏輯先註解，待確認後再啟用
        gender: null,
        birth_date: '',
        health_condition: '',
        education: null,
        occupation: '',
        precepts: '',
        observing_eight_precepts: '',
        skills: '',
        volunteer_interest: 'N',
        volunteer_or_staff_role: '',
        agreement_signed: 'N',
        agreement_signed_at: null,
        how_heard: '',
        attended_times: 0,
        transportation_go: 'self',
        transportation_return: 'self',
        applicant_remarks: ''
      },
      ui: {
        health_options: [],
        health_chronic: { checked: false, value: '' },
        health_special: { checked: false, value: '' },
        
        precepts_none: false,
        precepts_options: [],
        precepts_history: { checked: false, value: '' },
        precepts_ordination_date: '',
        
        how_heard_options: [],
        how_heard_other: { checked: false, value: '' },
        
        skills_options: [],
        skills_other: { checked: false, value: '' },

        // 照片上傳暫時隱藏，相關邏輯先註解，待確認後再啟用
        // photo: null,
        // photo_preview: null
      },
      constant: null
    };
  },
  methods: {
    async setConstant() { 
      const item = await this.$constantApiService.getFromMemberSystem()
      this.constant = item.data
    },
    // 照片上傳暫時隱藏，相關邏輯先註解，待確認後再啟用
    // handlePhotoChange(e) {
    //   const file = e.target.files[0];
    //   if (file) {
    //     this.ui.photo = file;
    //     const reader = new FileReader();
    //     reader.onload = (e) => {
    //       this.ui.photo_preview = e.target.result;
    //     };
    //     reader.readAsDataURL(file);
    //   } else {
    //     this.ui.photo = null;
    //     this.ui.photo_preview = null;
    //   }
    // },
    handleAgreementChange(value) {
      this.form.agreement_signed = value;
      this.form.agreement_signed_at = value === 'Y' ? dayjs().format('YYYY-MM-DD HH:mm:ss') : null;
    },
    syncPreceptsDate() {
      if (!this.ui.precepts_options.includes('full_ordination_precepts')) {
        this.ui.precepts_ordination_date = '';
      }
    },
    async nextStep() {
      const isValid = await this.$refs.observer.validate();
      if (isValid) {
        if (this.current_step < 3) {
          this.current_step++;
          this.$nextTick(() => {
            this.$refs.observer.reset();
          });
          window.scrollTo(0, 0);
        }
      } else {
        // 滾動到第一個錯誤欄位
        this.$nextTick(() => {
          const firstError = document.querySelector('.is-invalid, .invalid-feedback');
          if (firstError) {
             firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    },
    prevStep() {
      if (this.current_step > 1) {
        this.current_step--;
        window.scrollTo(0, 0);
      }
    },
    async onActualSubmit() {
      this.loading = true;
      try {
        if (this.form.agreement_signed === 'Y' && !this.form.agreement_signed_at) {
          this.form.agreement_signed_at = dayjs().format('YYYY-MM-DD HH:mm:ss');
        }

        // 準備提交的原始資料
        const submit_data = { ...this.form };
        
        // 輔助函式：將選擇的 key 轉為 Key-Value 結構
        const formatKeyValue = (selected_keys, value_map = {}, value_name = 'value') => {
            return selected_keys.map(key => {
              const obj = { key };
              if (value_map[key] !== undefined && value_map[key] !== null && value_map[key] !== '') {
                obj[value_name] = value_map[key];
              }
              return obj;
            });
        };

        // 處理健康狀況
        const health_value_map = {
          'chronic_disease': this.ui.health_chronic.value,
          'special_disease': this.ui.health_special.value
        };
        submit_data.health_condition = formatKeyValue(this.ui.health_options, health_value_map);

        // 處理戒法狀況
        const precepts_keys = [...this.ui.precepts_options];
        const precepts_date_map = {};
        // if (this.ui.precepts_none) precepts_keys.push('none');
        // if (this.ui.precepts_history.checked) {
        //   precepts_keys.push('history');
        //   precepts_value_map['history'] = this.ui.precepts_history.value;
        // }
        if (this.ui.precepts_options.includes('full_ordination_precepts') && this.ui.precepts_ordination_date) {
          precepts_date_map['full_ordination_precepts'] = this.ui.precepts_ordination_date;
        }
        submit_data.precepts = formatKeyValue(precepts_keys, precepts_date_map, 'ordination_date');

        // 處理消息來源
        const how_heard_value_map = {
          'other': this.ui.how_heard_other.value
        };
        submit_data.how_heard = formatKeyValue(this.ui.how_heard_options, how_heard_value_map);

        // 處理專長
        const skills_value_map = {
          'other': this.ui.skills_other.value
        };
        submit_data.skills = formatKeyValue(this.ui.skills_options, skills_value_map);

        // 準備 FormData 以支援檔案上傳，使用平舖方式讓後端可直接解析
        const form_data = new FormData();
        
        // 遞迴輔助函式：將巢狀物件/陣列轉換為扁平化的 FormData 鍵值 (例如 arr[0][id])
        const appendToFormData = (data, root_key) => {
          if (data instanceof File) {
            form_data.append(root_key, data);
          } else if (Array.isArray(data)) {
            data.forEach((item, index) => {
              appendToFormData(item, `${root_key}[${index}]`);
            });
          } else if (typeof data === 'object' && data !== null) {
            Object.keys(data).forEach(key => {
              appendToFormData(data[key], root_key ? `${root_key}[${key}]` : key);
            });
          } else {
            if (data !== undefined && data !== null) {
              form_data.append(root_key, data);
            }
          }
        };

        appendToFormData(submit_data, '');

        // 照片上傳暫時隱藏，相關邏輯先註解，待確認後再啟用
        // if (this.ui.photo) {
        //   form_data.append('photo', this.ui.photo);
        // }

        // form_data.forEach((value, key) => {
        //   console.log('----------------');
        //   console.log(`${key}:`);
        //   console.log(value);
        //   console.log(typeof value);
        //   console.log('----------------');
        // });
        // return;

        const result = await this.$eventRegistrationApiService.create(form_data);
        if (result) {
          this.$emit('success', result);
          this.submitted = true;
          // 報名成功後的後續處理
        } else {
          alert('報名失敗，請稍後再試。');
        }
      } catch (error) {
        console.error('Registration error:', error);
        alert('報名發生錯誤。');
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    'ui.precepts_options': {
      handler() {
        this.syncPreceptsDate();
      },
      deep: true
    },
    '$i18n.locale': {
      handler(newLocale) {
        console.log('in handler')
        localize(newLocale === 'tw' ? 'zh_TW' : 'en', locales[newLocale] || zh_TW);
      },
      immediate: true
    }
  },
  async created() {
    await this.setConstant()
  }
};
</script>

<style scoped>

  /* 調整步驟容器背景顏色 */
  .van-steps {
    background-color: transparent;
  }

  /* 調整步驟標題文字大小 */
  ::v-deep .van-step__title {
    font-size: 1.15rem !important;
    font-weight: bold;
  }
  /* 調整未完成步驟的圓點大小 */
  ::v-deep .van-step__circle {
    width: 1.2em !important;
    height: 1.2em !important;
  }
  /* 調整選中/完成步驟的圖示大小 */
  ::v-deep .van-step__icon {
    font-size: 1.2rem !important;
  }
  
  /* 增加容器高度以容納間距 */
  ::v-deep .van-steps--horizontal {
    padding-bottom: 25px;
  }
  /* 調整步驟標題文字間距 */
  ::v-deep .van-step--horizontal .van-step__title {
    margin-top: 10px;
    margin-bottom: 12px;
  }
  /* 調整步驟圓點容器間距 */
  ::v-deep .van-step--horizontal .van-step__circle-container {
    top: auto;
  }
  /* 調整步驟條容器間距 */
  ::v-deep .van-step--horizontal .van-step__line {
    top: auto;
  }

</style>
